import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, fmtINR, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Pencil, ChartLine, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY = { name: "", code: "", owner_user_id: "", description: "", status: "Active", annual_budget: "" };
const ALLOWED = new Set(["admin", "director"]);

export default function Projects() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [dialog, setDialog] = useState(null); // { mode:'create'|'edit', project? }
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [pnl, setPnl] = useState(null);
  const [pnlBusy, setPnlBusy] = useState(false);

  const canManage = user?.role === "admin" || user?.role === "director";

  const load = () =>
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});

  useEffect(() => {
    if (user && ALLOWED.has(user.role)) {
      load();
      api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const totalCentres = projects.reduce((s, p) => s + (p.centre_count || 0), 0);

  if (user && !ALLOWED.has(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  const openCreate = () => { setDialog({ mode: "create" }); setF(EMPTY); };
  const openEdit = (p) => {
    setDialog({ mode: "edit", project: p });
    setF({
      name: p.name || "", code: p.code || "",
      owner_user_id: p.owner_user_id || "",
      description: p.description || "", status: p.status || "Active",
      annual_budget: p.annual_budget != null ? String(p.annual_budget) : "",
    });
  };

  const submit = async () => {
    if (!f.name.trim()) { toast.error("Project name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: f.name.trim(),
        code: f.code.trim() || null,
        owner_user_id: f.owner_user_id || null,
        description: f.description.trim() || null,
        status: f.status,
        annual_budget: f.annual_budget === "" ? 0 : Number(f.annual_budget),
      };
      if (dialog.mode === "edit") {
        await api.patch(`/projects/${dialog.project.id}`, payload);
        toast.success(`Updated ${payload.name}`);
      } else {
        await api.post("/projects", payload);
        toast.success(`Created ${payload.name}`);
      }
      setDialog(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const openPnl = async (p) => {
    setPnlBusy(true);
    setPnl({ project: p, loading: true });
    try {
      const r = await api.get(`/projects/${p.id}/pnl`);
      setPnl(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setPnl(null);
    } finally {
      setPnlBusy(false);
    }
  };

  return (
    <div className="p-8 space-y-6" data-testid="projects-root">
      <div className="flex justify-between items-end flex-wrap gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Master</div>
          <h1 className="font-heading text-4xl mt-1 tracking-tight">Projects</h1>
          <p className="text-sm text-zinc-500 mt-1">
            {projects.length} project{projects.length === 1 ? "" : "s"} · {totalCentres} centre{totalCentres === 1 ? "" : "s"} mapped
          </p>
        </div>
        {canManage && (
          <Button onClick={openCreate} data-testid="new-project-btn"
            className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white h-10 gap-2">
            <Plus size={14} weight="bold" /> New Project
          </Button>
        )}
      </div>

      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left p-4">Name</th>
              <th className="text-left p-4">Owner</th>
              <th className="text-right p-4">Centres</th>
              <th className="text-right p-4">Annual Budget</th>
              <th className="text-right p-4">Spent</th>
              <th className="text-left p-4 min-w-[220px]">Consumption</th>
              <th className="text-left p-4">Status</th>
              <th className="text-right p-4">Actions</th>
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {projects.map((p) => {
              const budget = p.annual_budget || 0;
              const spent = p.spent || 0;
              const util = p.budget_utilisation || 0;
              const barColor = util > 100 ? "bg-rose-600" : util > 90 ? "bg-amber-500" : util > 75 ? "bg-amber-400" : "bg-emerald-600";
              const overBudget = budget > 0 && spent > budget;
              return (
              <tr key={p.id} className="border-t border-zinc-100 hover:bg-zinc-50"
                data-testid={`project-row-${p.name.replace(/\s+/g, "-").toLowerCase()}`}>
                <td className="p-4">
                  <div className="font-medium">{p.name}</div>
                  {p.code && <div className="font-mono text-[10px] text-zinc-500 mt-0.5">{p.code}</div>}
                </td>
                <td className="p-4 text-zinc-600">{p.owner_name || <span className="text-zinc-400">Unassigned</span>}</td>
                <td className="p-4 text-right">{p.centre_count}</td>
                <td className="p-4 text-right">{budget ? fmtINR(budget) : <span className="text-zinc-400">—</span>}</td>
                <td className={`p-4 text-right ${overBudget ? "text-rose-700 font-semibold" : "text-zinc-700"}`}
                  data-testid={`project-spent-${p.name.replace(/\s+/g, "-").toLowerCase()}`}>
                  {fmtINR(spent)}
                </td>
                <td className="p-4">
                  {budget > 0 ? (
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-zinc-100 relative overflow-hidden">
                        <div className={`h-full ${barColor} transition-all`}
                          style={{ width: `${Math.min(100, util)}%` }} />
                      </div>
                      <span className={`text-xs w-12 text-right ${overBudget ? "text-rose-700 font-medium" : "text-zinc-600"}`}
                        data-testid={`project-util-${p.name.replace(/\s+/g, "-").toLowerCase()}`}>
                        {util}%
                      </span>
                    </div>
                  ) : (
                    <span className="text-xs text-zinc-400">No budget set</span>
                  )}
                </td>
                <td className="p-4">
                  <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest border ${
                    p.status === "Active"
                      ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                      : "bg-zinc-100 text-zinc-600 border-zinc-300"
                  }`}>{p.status}</span>
                </td>
                <td className="p-4 text-right">
                  <div className="flex justify-end gap-3 items-center">
                    <button onClick={() => openPnl(p)}
                      className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-900"
                      data-testid={`project-pnl-${p.name.replace(/\s+/g, "-").toLowerCase()}`}>
                      <ChartLine size={12} /> P&L
                    </button>
                    {canManage && (
                      <>
                        <button onClick={() => openEdit(p)}
                          className="inline-flex items-center gap-1 text-xs text-zinc-600 hover:text-zinc-900"
                          data-testid={`edit-project-${p.name.replace(/\s+/g, "-").toLowerCase()}`}>
                          <Pencil size={12} /> Edit
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
              );
            })}
            {projects.length === 0 && (
              <tr>
                <td colSpan={8} className="p-12 text-center text-sm text-zinc-500" data-testid="projects-empty">
                  No projects yet — create your first to start grouping centres.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="bg-white rounded-sm max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {dialog?.mode === "edit" ? `Edit Project · ${dialog.project?.name}` : "Create Project"}
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              A project groups one or more centres for consolidated P&L reporting.
              Renaming a project cascades to every mapped centre automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest">Name *</Label>
              <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
                className="rounded-sm h-10 mt-2" data-testid="project-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Code</Label>
              <Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })}
                className="rounded-sm h-10 mt-2" data-testid="project-code" placeholder="e.g. SHB" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Status</Label>
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                <SelectTrigger className="rounded-sm h-10 mt-2" data-testid="project-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest">Owner</Label>
              <Select value={f.owner_user_id || "__none__"} onValueChange={(v) => setF({ ...f, owner_user_id: v === "__none__" ? "" : v })}>
                <SelectTrigger className="rounded-sm h-10 mt-2" data-testid="project-owner"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Unassigned</SelectItem>
                  {users.filter((u) => u.is_active !== false).map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest">Annual Budget (₹)</Label>
              <Input
                type="number" min="0" step="1000"
                value={f.annual_budget}
                onChange={(e) => setF({ ...f, annual_budget: e.target.value })}
                className="rounded-sm h-10 mt-2"
                data-testid="project-annual-budget"
                placeholder="0 = no budget"
              />
              <p className="text-[10px] text-zinc-500 mt-1">
                Project-level annual cap. Consumption = sum of realised expenses across every centre mapped to this project.
              </p>
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest">Description</Label>
              <Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })}
                className="rounded-sm h-10 mt-2" data-testid="project-description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="save-project"
              className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white">
              {saving ? "Saving…" : dialog?.mode === "edit" ? "Save Changes" : "Create Project"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* P&L drill-down */}
      {pnl && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setPnl(null)} data-testid="project-pnl-modal">
          <div className="bg-white max-w-4xl w-full max-h-[85vh] overflow-auto p-6"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-baseline justify-between border-b border-zinc-200 pb-3 mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Project P&L</div>
                <h2 className="font-heading text-2xl">{pnl.project?.name}</h2>
                {pnl.project?.owner_name && (
                  <div className="text-xs text-zinc-500 mt-0.5">Owner: {pnl.project.owner_name}</div>
                )}
              </div>
              <button onClick={() => setPnl(null)}
                className="text-xs uppercase tracking-widest text-zinc-500 hover:text-black inline-flex items-center gap-1"
                data-testid="project-pnl-close">
                <X size={12} /> Close
              </button>
            </div>

            {pnlBusy && <div className="text-center text-zinc-400 py-6">Loading…</div>}

            {!pnlBusy && pnl.totals && (
              <>
                <div className="grid grid-cols-4 gap-3 mb-6">
                  <KPI label="Total Income" value={fmtINR(pnl.totals.income)} tone="positive" testid="project-pnl-income" />
                  <KPI label="Total Expense" value={fmtINR(pnl.totals.expense)} testid="project-pnl-expense" />
                  <KPI label="Total Assets" value={fmtINR(pnl.totals.assets)} testid="project-pnl-assets" />
                  <KPI label="P/L" value={fmtINR(pnl.totals.profit)}
                    tone={pnl.totals.profit >= 0 ? "positive" : "danger"} testid="project-pnl-profit" />
                </div>
                <div className="grid grid-cols-3 gap-3 mb-6">
                  <KPI label="Project Annual Budget"
                    value={pnl.totals.annual_budget ? fmtINR(pnl.totals.annual_budget) : "Not set"}
                    small testid="project-pnl-budget" />
                  <KPI label="Budget Remaining"
                    value={pnl.totals.annual_budget ? fmtINR(pnl.totals.budget_remaining) : "—"}
                    small tone={pnl.totals.budget_remaining < 0 ? "danger" : pnl.totals.budget_utilisation > 75 ? "warn" : "positive"}
                    testid="project-pnl-budget-remaining" />
                  <KPI label="Budget Utilisation"
                    value={pnl.totals.annual_budget ? `${pnl.totals.budget_utilisation}%` : "—"}
                    small tone={pnl.totals.budget_utilisation > 100 ? "danger" : pnl.totals.budget_utilisation > 90 ? "warn" : pnl.totals.budget_utilisation > 75 ? "warn" : "default"}
                    testid="project-pnl-budget-util" />
                </div>

                {pnl.totals.annual_budget > 0 && (
                  <div className="mb-6" data-testid="project-pnl-budget-bar">
                    <div className="flex justify-between text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                      <span>Spent {fmtINR(pnl.totals.expense)}</span>
                      <span>of {fmtINR(pnl.totals.annual_budget)}</span>
                    </div>
                    <div className="h-3 bg-zinc-100 relative overflow-hidden">
                      <div className={`h-full transition-all ${
                        pnl.totals.budget_utilisation > 100 ? "bg-rose-600"
                        : pnl.totals.budget_utilisation > 90 ? "bg-amber-500"
                        : pnl.totals.budget_utilisation > 75 ? "bg-amber-400"
                        : "bg-emerald-600"
                      }`} style={{ width: `${Math.min(100, pnl.totals.budget_utilisation)}%` }} />
                    </div>
                    {pnl.totals.budget_utilisation > 100 && (
                      <div className="text-xs text-rose-700 mt-1">
                        Over budget by {fmtINR(pnl.totals.expense - pnl.totals.annual_budget)}
                      </div>
                    )}
                  </div>
                )}

                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Centre breakdown</div>
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-widest text-zinc-500">
                    <tr>
                      <th className="text-left py-2">Code</th>
                      <th className="text-left py-2">Centre</th>
                      <th className="text-right py-2">Income</th>
                      <th className="text-right py-2">Expense</th>
                      <th className="text-right py-2">P/L</th>
                      <th className="text-right py-2">Util.</th>
                    </tr>
                  </thead>
                  <tbody className="tabular-nums">
                    {(pnl.centres || []).length === 0 && (
                      <tr><td colSpan={6} className="py-6 text-center text-zinc-400">No centres in this project.</td></tr>
                    )}
                    {(pnl.centres || []).map((c) => (
                      <tr key={c.centre_id} className="border-t border-zinc-100">
                        <td className="py-2 font-mono text-xs">{c.code}</td>
                        <td className="py-2 font-medium">{c.name}</td>
                        <td className="py-2 text-right">{fmtINR(c.income)}</td>
                        <td className="py-2 text-right">{fmtINR(c.expense)}</td>
                        <td className={`py-2 text-right font-medium ${c.profit >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{fmtINR(c.profit)}</td>
                        <td className="py-2 text-right">{c.utilisation}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function KPI({ label, value, tone = "default", small, testid }) {
  const toneClass = {
    default: "text-zinc-900",
    positive: "text-emerald-700",
    danger: "text-rose-700",
    warn: "text-amber-700",
  }[tone];
  return (
    <div className="p-4 border border-zinc-200 bg-zinc-50" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`${small ? "text-xl" : "text-2xl"} font-heading mt-1 tabular-nums ${toneClass}`}>{value}</div>
    </div>
  );
}
